"use client";

import { Calendar, ChevronLeft, Clock, Share2, User } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div className="h-12 w-3/4 animate-pulse bg-muted/40" />
        <div className="h-6 w-1/2 animate-pulse bg-muted/30" />
        <div className="aspect-[16/9] w-full animate-pulse bg-muted/30" />
        <div className="space-y-3">
          <div className="h-4 w-full animate-pulse bg-muted/30" />
          <div className="h-4 w-full animate-pulse bg-muted/30" />
          <div className="h-4 w-5/6 animate-pulse bg-muted/30" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-muted-foreground">Unable to load this article right now.</p>
      </div>
    );
  }

  const post = posts && posts.length > 0 ? posts[0] : null;

  if (!post) {
    return (
      <div className="mx-auto max-w-2xl text-center">
        <p className="text-muted-foreground">No article found.</p>
      </div>
    );
  }

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorName = post.author?.name || "GST Engineering";
  const authorAvatar = post.author?.avatar
    ? post.author.avatar["96"] || Object.values(post.author.avatar)[0]
    : "";

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <div className="mb-10">
        <Link
          to="/HomePage"
          className="text-blue-900 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest hover:text-blue-700 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Home
        </Link>
      </div>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="border border-blue-900/40 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-900"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default mb-8 text-4xl font-bold leading-[1.05] tracking-tight text-blue-950 md:text-6xl lg:text-7xl"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="mb-12 flex flex-wrap items-center gap-x-6 gap-y-3 border-y border-blue-900/10 py-5">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9 border border-blue-900/20">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="bg-blue-100 text-blue-900 text-xs font-semibold">
              {authorName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-widest text-blue-700">
              Authored by
            </span>
            <span className="text-sm font-semibold text-blue-950">{authorName}</span>
          </div>
        </div>

        <Separator orientation="vertical" className="hidden h-10 md:block" />

        {formattedDate && (
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <Calendar className="h-4 w-4 text-blue-600" />
            <span>{formattedDate}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-blue-800">
          <Clock className="h-4 w-4 text-blue-600" />
          <span>5 min read</span>
        </div>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            className="border-blue-900/20 bg-transparent text-blue-900 hover:bg-blue-600 hover:text-white hover:border-blue-600"
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        </div>
      </div>

      {}
      {post.featuredImage && (
        <figure className="mb-12 overflow-hidden rounded-lg shadow-lg ring-1 ring-blue-900/10">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
              loading="lazy"
              decoding="async"
            />
          </AspectRatio>
        </figure>
      )}

      {}
      {post.excerpt && (
        <div
          className="font-default mb-10 border-l-2 border-blue-600 pl-6 text-xl italic leading-relaxed text-blue-800 md:text-2xl"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {post.content && (
        <div
          className="font-default prose prose-blue max-w-none text-lg leading-relaxed text-blue-950/90 [&_a]:text-blue-700 [&_a]:underline [&_h2]:font-default [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:text-blue-950 [&_h3]:font-default [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-blue-950 [&_p]:mb-6 [&_img]:my-8 [&_blockquote]:border-l-2 [&_blockquote]:border-blue-600 [&_blockquote]:pl-6 [&_blockquote]:italic [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-6"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-blue-900/10 pt-8">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-700">
            Industry Tags
          </span>
          {post.tags.map((tag, i) => (
            <span
              data-index={i}
              key={tag.id}
              className="border border-blue-900/10 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-900 hover:border-blue-600 hover:text-blue-700 transition-colors"
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      {}
      <Card className="mt-14 border border-blue-900/10 bg-white shadow-md rounded-lg">
        <CardContent className="flex flex-col items-start gap-6 p-8 md:flex-row md:items-center">
          <Avatar className="h-20 w-20 border-2 border-blue-600">
            {authorAvatar && <AvatarImage src={authorAvatar} alt={authorName} />}
            <AvatarFallback className="bg-blue-100 text-blue-900 text-lg font-bold">
              {authorName.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="mb-1 text-xs uppercase tracking-[0.25em] text-blue-600">
              Technical Lead
            </div>
            <h3 className="font-default mb-2 text-2xl font-bold text-blue-950">
              {authorName}
            </h3>
            <p className="font-default text-base text-blue-800">
              Engineering expert at Global Standard Technology, specializing in precision converting solutions for the aerospace and automotive sectors.
            </p>
          </div>
          <Button className="bg-blue-900 text-white hover:bg-blue-800 self-start md:self-center rounded-lg">
            <User className="h-4 w-4" />
            View All Insights
          </Button>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div data-index={i} key={i} className="space-y-4">
            <div className="aspect-[4/3] w-full animate-pulse bg-blue-100" />
            <div className="h-5 w-3/4 animate-pulse bg-blue-100" />
            <div className="h-4 w-1/2 animate-pulse bg-blue-100" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-center text-blue-800">No related technical articles available.</p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.slice(0, 3).map((post, i) => (
        <article
          data-index={i}
          key={post.id}
          className="group border border-blue-900/10 bg-white shadow-md rounded-lg transition-all hover:border-blue-600 hover:shadow-lg"
        >
          <Link to={post.link || "#"} className="block">
            {post.featuredImage ? (
              <AspectRatio ratio={4 / 3}>
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 rounded-t-lg"
                  loading="lazy"
                  decoding="async"
                />
              </AspectRatio>
            ) : (
              <AspectRatio ratio={4 / 3}>
                <div className="flex h-full w-full items-center justify-center bg-blue-50 rounded-t-lg">
                  <span className="font-default text-3xl font-bold text-blue-300">
                    GST
                  </span>
                </div>
              </AspectRatio>
            )}
            <div className="p-6">
              {post.categories && post.categories.length > 0 && (
                <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
                  {post.categories[0].name}
                </span>
              )}
              <h3
                className="font-default mb-3 text-xl font-bold leading-tight text-blue-950 group-hover:text-blue-700 transition-colors"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
              {post.date && (
                <div className="flex items-center gap-2 text-xs text-blue-700">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>
                    {new Date(post.date).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
              )}
            </div>
          </Link>
        </article>
      ))}
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({ post_type: "post", per_page: 3 });

  return (
    <section data-section-id="5155"
      id="single-post-template-section"
      className="bg-slate-50 text-blue-950 relative overflow-hidden py-20 px-4 md:py-28"
    >
      {}
      <div className="pointer-events-none absolute -top-40 right-0 h-96 w-96 bg-blue-100 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-96 w-96 bg-blue-50 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {}
        <div className="mx-auto mb-12 max-w-4xl">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-blue-600" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-blue-600">
              Engineering Insights
            </span>
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="mt-24">
          <div className="mb-12 flex items-end justify-between gap-6 border-b border-blue-900/10 pb-6">
            <div>
              <span className="mb-3 block text-xs font-bold uppercase tracking-[0.3em] text-blue-600">
                Related Content
              </span>
              <h2 className="font-default text-3xl font-bold tracking-tight text-blue-950 md:text-5xl">
                Technical Resources
              </h2>
            </div>
            <Link
              to="/ProductsPage"
              className="text-blue-900 hidden text-sm font-semibold uppercase tracking-widest hover:text-blue-700 transition-colors md:block"
            >
              View All →
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsConsumer />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}